import { type ReactNode } from 'react';
import {
  AlertStatusIcon,
  CloseIcon,
  InfoStatusIcon,
  ShieldIcon,
} from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export type SecurityCalloutTone = 'info' | 'warning' | 'security';
export type SecurityCalloutIcon = 'shield' | 'warning' | 'info';

export interface SecurityCalloutProps {
  readonly tone?: SecurityCalloutTone;
  readonly icon?: SecurityCalloutIcon;
  readonly title?: ReactNode;
  readonly children: ReactNode;
  readonly onDismiss?: () => void;
  readonly className?: string;
}

const TONE: Record<SecurityCalloutTone, { box: string; iconClass: string }> = {
  info: { box: 'bg-info/10 border-info/30', iconClass: 'text-info' },
  warning: { box: 'bg-warning/10 border-warning/40', iconClass: 'text-warning' },
  security: { box: 'bg-accent-soft border-accent/30', iconClass: 'text-accent' },
};

const ICON = {
  shield: ShieldIcon,
  warning: AlertStatusIcon,
  info: InfoStatusIcon,
} as const;

/**
 * F06 — warning banner with shield + legal copy.
 *
 * Used for MoET disclosure on AdminLoginScreen and the session-expiring /
 * account-locked panels in edge artboards. Dismissible if `onDismiss` is given.
 */
export function SecurityCallout({
  tone = 'security',
  icon = 'shield',
  title,
  children,
  onDismiss,
  className,
}: SecurityCalloutProps) {
  const { box, iconClass } = TONE[tone];
  const Icon = ICON[icon];

  return (
    <div
      role="note"
      className={cn('flex items-start gap-3 rounded-xl border p-3.5', box, className)}
    >
      <div className={cn('shrink-0 mt-0.5', iconClass)} aria-hidden="true">
        <Icon size={18} color="currentColor" />
      </div>
      <div className="flex-1 min-w-0 text-[12.5px] leading-relaxed text-ink-2">
        {title ? <div className="text-[13px] font-bold text-ink mb-1">{title}</div> : null}
        {children}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 -m-1 size-7 inline-flex items-center justify-center rounded-md text-ink-2 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40"
        >
          <CloseIcon size={14} color="currentColor" />
        </button>
      ) : null}
    </div>
  );
}

export default SecurityCallout;
